import React, { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { TruckLoader } from '../common/truck';
import { COLORS, FONTS } from '../../styles/tokens';

const stop = e => e.stopPropagation();

// Liten bekreftelsesdialog midt på skjermen (alertdialog) mot feiltrykk.
// Fokus starter på bekreft-knappen; Escape og trykk utenfor avbryter (ikke mens det sendes).
// error vises i dialogen (role="alert") og dialogen blir stående åpen.
export default function ConfirmDialog({
  title, children, confirmLabel, busyLabel = 'Sender…', cancelLabel = 'Avbryt',
  busy = false, error = '', danger = false, hideConfirm = false, onConfirm, onCancel,
}) {
  const confirmRef = useRef(null);
  const cancelRef = useRef(null);
  const id = useRef(`cd-${Math.random().toString(36).slice(2, 8)}`).current;

  useEffect(() => { (confirmRef.current ?? cancelRef.current)?.focus(); }, []);

  useEffect(() => {
    const onKey = e => { if (e.key === 'Escape' && !busy) onCancel(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [busy, onCancel]);

  // Tab går bare mellom dialogens egne knapper
  function onKeyDown(e) {
    if (e.key !== 'Tab') return;
    const items = [cancelRef.current, confirmRef.current].filter(el => el && !el.disabled);
    if (items.length === 0) { e.preventDefault(); return; }
    const first = items[0], last = items[items.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }

  return ReactDOM.createPortal(
    <div
      className="driver-ui"
      style={styles.overlay}
      onClick={e => { e.stopPropagation(); if (e.target === e.currentTarget && !busy) onCancel(); }}
      onTouchStart={stop} onTouchEnd={stop} onMouseDown={stop}
    >
      <div
        role="alertdialog" aria-modal="true" aria-labelledby={`${id}-t`} aria-describedby={`${id}-d`}
        style={styles.box} onKeyDown={onKeyDown}
      >
        <h2 id={`${id}-t`} style={styles.title}>{title}</h2>
        <div id={`${id}-d`} style={styles.body}>{children}</div>
        {error && <p role="alert" style={styles.error}>{error}</p>}
        <div style={styles.btns}>
          <button ref={cancelRef} type="button" className="tippi-btn-secondary" style={styles.cancel} disabled={busy} onClick={onCancel}>
            {cancelLabel}
          </button>
          {!hideConfirm && (
            <button
              ref={confirmRef} type="button" className="tippi-btn" aria-busy={busy || undefined}
              style={{ ...styles.confirm, ...(danger ? styles.danger : {}), ...(busy ? styles.busy : {}) }}
              disabled={busy} onClick={onConfirm}
            >
              {busy ? <><TruckLoader size={24} decorative />{busyLabel}</> : confirmLabel}
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}

const pill = { flex: 1, minWidth: 0, height: 48, borderRadius: 24, fontSize: 15, fontWeight: 700, cursor: 'pointer', padding: '0 10px' };

const styles = {
  overlay: { position: 'fixed', inset: 0, background: 'rgba(20,20,30,0.5)', zIndex: 1600, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 },
  box:     { background: COLORS.white, color: COLORS.textDark, width: '100%', maxWidth: 340, borderRadius: 20, padding: '22px 20px 18px', boxShadow: '0 12px 40px rgba(0,0,0,0.3)' },
  title:   { fontFamily: FONTS.display, fontWeight: 700, fontSize: 28, lineHeight: 1.1, margin: '0 0 8px', color: COLORS.textDark },
  body:    { fontSize: 15, lineHeight: 1.45, color: COLORS.textMid, margin: '0 0 16px' },
  error:   { background: COLORS.dangerBg, color: COLORS.dangerText, padding: '10px 12px', borderRadius: 10, fontSize: 14, margin: '0 0 14px' },
  btns:    { display: 'flex', gap: 10 },
  cancel:  { ...pill, border: `2px solid ${COLORS.primary}`, background: COLORS.white, color: COLORS.textDark },
  confirm: { ...pill, border: 'none', background: COLORS.primary, color: COLORS.onPrimary, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 },
  danger:  { background: COLORS.danger, color: '#fff' },
  busy:    { opacity: 0.7 },
};
